import type { GridApi } from 'ag-grid-community';
import { cloneDeep, get, set } from 'lodash-es';
import { EMPTY_ARRAY } from '../../utils';

/** A constant key used for the internal row ID. */
export const ENTITY_INTERNAL_ROW_ID = 'ENTITY_INTERNAL_ROW_ID';
export const GLOBAL_ENTITY_INTERNAL_ID = 'GLOBAL_ENTITY_INTERNAL_ID';

/**
 * EntityAdminClass provides inheritance for entity data management.
 *
 * Implements a three-tier inheritance system:
 * 1. Entity (highest priority) - The actual entity data
 * 2. Parent Entity (medium priority) - Parent entity for hierarchical relationships
 * 3. Global Entity (lowest priority) - Global fallback values for all entities
 *
 * Supports deep nested object inheritance using dot notation (e.g., 'config.timeout.connect').
 */
export class EntityAdminClass<T extends Record<string, any>> {
  private parentKey: T[keyof T] | typeof GLOBAL_ENTITY_INTERNAL_ID;

  /**
   * Retrieves a field value from the target object using dot notation.
   */
  private getField(target: T | undefined, field: keyof T | undefined): T[keyof T] | undefined {
    if (field == null || target == null) {
      return undefined;
    }
    return get(target, field);
  }

  /**
   * Gets a field value using the inheritance chain: entity -> parent -> global.
   * If inheritance is disabled, only returns the entity's own value.
   */
  private getFieldValueWithInheritance(field: keyof T): T[keyof T] | undefined {
    // If inheritance is disabled, only return entity's own value
    if (!this.allowInheritance) {
      return this.getField(this.entity, field);
    }

    return (
      this.getField(this.entity, field) ??
      this.getField(this.parentEntity, field) ??
      this.getField(this.globalEntity, field)
    );
  }

  /**
   * Sets a field value on the target entity using dot notation.
   */
  private setField(target: T, field: keyof T, value: T[keyof T]): boolean {
    if (field == null || target == null) {
      return false;
    }
    set(target, field, value);
    return true;
  }

  /**
   * Checks if a value should be wrapped in an inheritance proxy.
   */
  private shouldCreateProxy(value: unknown): value is Record<string, unknown> {
    return value != null && typeof value === 'object' && !Array.isArray(value);
  }

  /**
   * Creates a recursive proxy for nested objects to enable inheritance at all levels.
   *
   * @param value - The value to create a proxy for
   * @param basePath - Builds the dot notation path as we go deeper ('' -> 'config' -> 'config.timeouts')
   * @returns The original value (if primitive/array) or a proxy (if object)
   */
  private createInheritanceProxy(value: unknown, basePath: string = ''): unknown {
    // Return primitives and arrays as-is (no inheritance within arrays)
    if (!this.shouldCreateProxy(value)) {
      return value;
    }

    // No parent/global inheritance: nested proxies add cost and break consumers that walk
    // plain object graphs (e.g. JSON.stringify on grid export for frozen nested props).
    // We do not rely here on the nested-proxy fallback (missing key on nested object →
    // getFieldValueWithInheritance(fullPath)); Counterparties and similar grids use
    // allowInheritance=false and only read defined paths (e.g. params.data?.marketAccount?.agreement).
    if (!this.allowInheritance) {
      return value;
    }

    const targetObject = value as Record<string, unknown>;

    return new Proxy(targetObject, {
      get: (target, property: string | symbol) => {
        // Handle non-string properties normally
        if (typeof property !== 'string') {
          return Reflect.get(target, property);
        }

        // Build the full property path: if we're at 'config' and accessing 'timeout', path becomes 'config.timeout'
        const currentPath = basePath ? `${basePath}.${property}` : property;

        // First try to get the property from the current object
        if (property in target && target[property] != null) {
          const desc = Object.getOwnPropertyDescriptor(target, property);
          if (desc && 'value' in desc && !desc.configurable && !desc.writable) {
            return desc.value;
          }
          return this.createInheritanceProxy(target[property], currentPath);
        }

        // If not found, check the inheritance chain using the full path
        const inheritedValue = this.getFieldValueWithInheritance(currentPath as keyof T);
        return this.createInheritanceProxy(inheritedValue, currentPath);
      },

      has: (target, property) => {
        if (typeof property !== 'string') {
          return Reflect.has(target, property);
        }

        const currentPath = basePath ? `${basePath}.${property}` : property;
        // If inheritance is disabled, only check the current object
        if (!this.allowInheritance) {
          return Reflect.has(target, property);
        }
        return Reflect.has(target, property) || this.getFieldValueWithInheritance(currentPath as keyof T) != null;
      },
    });
  }

  /**
   * Creates an EntityAdminClass instance with inheritance capabilities.
   * Returns a Proxy that intercepts property access to enable field inheritance.
   *
   * @param entity - The entity data
   * @param entityIDField - The field that identifies the entity
   * @param childIDField - The field that identifies child entities (for hierarchical data)
   * @param parentMapRef - Reference to the map containing parent and global entities
   * @param allowInheritance - Whether to allow inheritance from parent/global entities. Defaults to true
   */
  constructor(
    private entity: T,
    public entityIDField: keyof T,
    public childIDField: keyof T | undefined,
    public parentMapRef: React.MutableRefObject<Map<keyof T, T>>,
    public allowInheritance: boolean = true
  ) {
    this.parentKey = this.isChildRow ? this.getField(entity, entityIDField)! : GLOBAL_ENTITY_INTERNAL_ID;

    return new Proxy(this, {
      get: (target, property: string | symbol) => {
        // Check if property exists on the EntityAdminClass instance
        if (property in target) {
          return target[property as keyof this];
        }

        // Apply inheritance for entity fields
        if (typeof property !== 'string') {
          return undefined;
        }

        const field: keyof T = property satisfies keyof T;
        const value = target.getFieldValueWithInheritance(field);
        return target.createInheritanceProxy(value, property);
      },

      set: (target, property: string | symbol, value) => {
        if (typeof property !== 'string') {
          return false;
        }

        // Update the entity when setting fields
        const field: keyof T = property satisfies keyof T;
        return target.setField(target.entity, field, value);
      },
    });
  }

  /**
   * Gets the internal row ID based on entityIDField and childIDField.
   */
  get [ENTITY_INTERNAL_ROW_ID](): string {
    const entityID = this.getField(this.entity, this.entityIDField)!;
    const childEntityID = this.childIDField ? this.getField(this.entity, this.childIDField) : undefined;
    return childEntityID ? `${entityID}-${childEntityID}` : `${entityID}`;
  }

  /**
   * Gets the parent entity. For parents, returns global entity. For children, returns their parent.
   */
  get parentEntity(): T | undefined {
    return this.parentMapRef.current.get(this.parentKey);
  }

  /**
   * Gets the global entity that provides fallback values for all entities.
   */
  get globalEntity(): T | undefined {
    return this.parentMapRef.current.get(GLOBAL_ENTITY_INTERNAL_ID);
  }

  /**
   * Checks if a field value is inherited from another entity.
   */
  isFieldInherited(field: keyof T): boolean {
    if (this.getField(this.entity, field) != null) {
      return false;
    }
    return this.getField(this.parentEntity, field) != null || this.getField(this.globalEntity, field) != null;
  }

  /**
   * Checks if the current entity is a child row.
   */
  get isChildRow(): boolean {
    return this.childIDField != null && this.getField(this.entity, this.childIDField) != null;
  }

  /**
   * Checks if the current entity is a parent row.
   */
  get isParentRow(): boolean {
    return !this.isChildRow;
  }

  getAllChildren(gridApi: GridApi): EntityAdminClass<T>[] {
    if (this.isParentRow) {
      return gridApi.getRowNode(this[ENTITY_INTERNAL_ROW_ID])?.allLeafChildren?.map(node => node.data) ?? EMPTY_ARRAY;
    }
    return EMPTY_ARRAY;
  }

  /** Gets the underlying entity data. */
  get data(): T {
    return this.entity;
  }

  /**
   * Returns a new EntityAdminClass instance with a deep clone of the underlying entity data.
   * Preserves all metadata (entityIDField, childIDField, parentMapRef, allowInheritance).
   * Useful when you need to modify the entity without affecting the original.
   */
  clone(): EntityAdminClass<T> {
    return new EntityAdminClass(
      cloneDeep(this.entity),
      this.entityIDField,
      this.childIDField,
      this.parentMapRef,
      this.allowInheritance
    );
  }

  /** @deprecated Use `this.data` instead. */
  toJSON(): T {
    return this.data;
  }
}
